import { FC, useMemo, useCallback, useEffect } from 'react'
import styles from 'components/features/pfm/MixedAccountDetailsPane/MixedAccountDetailsPane.styl'
import Scrollable from 'components/atoms/Scrollable'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { useStoreSelector, useShallowSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { MixedAccountDetailsPane as MixedAccountDetailsPaneType } from 'interfaces/floatingPanes'
import { extractLinkageEntityFromCid } from 'helpers/linkageHelpers'
import { ManualAccountCreation } from 'components/molecules/ManualAccountCreation'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { createLinkageSessionByIdSelector } from 'selectors/linkageSelectors'
import { LinkageAuthentication } from 'components/molecules/LinkageAuthentication'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { killLinkageSessionActionCreator } from 'actions/linkageActions'

const TYPED_PANE: Pick<MixedAccountDetailsPaneType, 'type'> = {
  type: 'mixed-account-details',
}

type MixedAccountDetailsPaneProps = {}

export const MixedAccountDetailsPane: FC<MixedAccountDetailsPaneProps> = (props) => {
  const paneSelector = useMemo(createFloatingPaneByTypeSelector, [])
  const pane = useStoreSelector((state) => paneSelector(state, TYPED_PANE)) as MixedAccountDetailsPaneType
  const sessionId = pane ? pane.sessionId : null

  return (
    <MixedAccountDetailsPaneInner
      key={ sessionId || '' }
      pane={ pane }
      { ...props }
    />
  )
}

type MixedAccountDetailsPaneInnerProps =
  & MixedAccountDetailsPaneProps
  & {
    pane: MixedAccountDetailsPaneType
  }
const MixedAccountDetailsPaneInner: FC<MixedAccountDetailsPaneInnerProps> = (props) => {
  const { pane } = props
  const sessionId = pane ? pane.sessionId : null
  const withSessionId = useMemo(() => ({
    sessionId,
  }), [sessionId])
  const sessionByIdSelector = useMemo(createLinkageSessionByIdSelector, [])
  const session = useShallowSelector((state) => sessionByIdSelector(state, withSessionId))

  const dispatch = useStoreDispatch()
  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('mixed-account-details'))
  }, [dispatch])

  useEffect(() => {
    return () => {
      if (sessionId) {
        dispatch(killLinkageSessionActionCreator(sessionId))
      }
    }
  }, [sessionId, dispatch])

  const linkageEntity = session ? extractLinkageEntityFromCid(session.cid) : null
  if (!sessionId || !linkageEntity) {
    return null
  }

  // TODO: on close for linkage authentication?
  const bodyChild = linkageEntity.type === 'manual-balance-account'
    ? (
      <ManualAccountCreation
        sessionId={ sessionId }
        onClose={ handleClose }
      />
    )
    : (
      <LinkageAuthentication
        sessionId={ sessionId }
      />
    )

  const loadingChild = session && session.loading && (
    <LoadingOverlay />
  )

  return (
    <FloatingPaneLayout
      type='mixed-account-details'
      onClose={ handleClose }
    >
      <section className={ styles.accountLinkageModal } data-qa-marker='accounts-linkage-modal'>
        <Scrollable contentClassName={ styles.body }>
          <div className={ styles.contentWrapper }>
            { bodyChild }
          </div>
        </Scrollable>
      </section>
      { loadingChild }
    </FloatingPaneLayout>
  )
}


